import React from "react";
import { usePageTitle } from "../hooks/usePageTitle";

const Privacy = () => {
  usePageTitle("Privacy Policy");

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-white mb-6">Privacy Policy</h1>
      <p className="text-gray-400 text-sm mb-8">
        Last updated: January 10, 2026
      </p>

      <div className="space-y-8 text-gray-300">
        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            1. Introduction
          </h2>
          <p>
            Welcome to Collect Arena ("we," "our," or "us"). We respect your
            privacy and are committed to protecting your personal data. This
            privacy policy will inform you about how we handle your personal
            data when you visit our website and tell you about your privacy
            rights.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            2. Information We Collect
          </h2>
          <p className="mb-3">We collect and process the following data:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Account Information:</strong> Username, email address, and
              password (encrypted)
            </li>
            <li>
              <strong>Usage Data:</strong> Your quiz progress, completed lists,
              and guesses
            </li>
            <li>
              <strong>Technical Data:</strong> IP address, browser type, device
              information
            </li>
            <li>
              <strong>Cookies:</strong> Essential cookies for authentication and
              user preferences
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            3. How We Use Your Information
          </h2>
          <p className="mb-3">We use your data to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Provide and maintain our service</li>
            <li>Track your quiz progress and save your achievements</li>
            <li>Improve our platform and user experience</li>
            <li>Send important service updates (if applicable)</li>
            <li>Protect against fraud and abuse</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            4. Legal Basis for Processing (GDPR)
          </h2>
          <p className="mb-3">
            We process your data under the following legal bases:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Consent:</strong> You have given consent for specific
              purposes
            </li>
            <li>
              <strong>Contract:</strong> Processing is necessary to provide our
              service
            </li>
            <li>
              <strong>Legitimate Interests:</strong> To improve our platform and
              prevent fraud
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            5. Data Sharing
          </h2>
          <p>
            We do NOT sell, trade, or share your personal data with third
            parties. Your data is stored securely on our servers and is only
            accessible to authorized personnel for service maintenance.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            6. Your Rights (GDPR)
          </h2>
          <p className="mb-3">Under GDPR, you have the right to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Access:</strong> Request a copy of your personal data
            </li>
            <li>
              <strong>Rectification:</strong> Correct inaccurate or incomplete
              data
            </li>
            <li>
              <strong>Erasure:</strong> Request deletion of your data ("right to
              be forgotten")
            </li>
            <li>
              <strong>Data Portability:</strong> Receive your data in a
              machine-readable format
            </li>
            <li>
              <strong>Withdraw Consent:</strong> Withdraw consent at any time
            </li>
            <li>
              <strong>Object:</strong> Object to processing based on legitimate
              interests
            </li>
          </ul>
          <p className="mt-3">
            To exercise these rights, please contact us at{" "}
            <a
              href="mailto:privacy@collectarena.com"
              className="text-blue-400 hover:text-blue-300"
            >
              privacy@collectarena.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            7. Data Retention
          </h2>
          <p>
            We retain your personal data only as long as necessary to provide
            our services and comply with legal obligations. When you delete your
            account, all personal data is permanently removed from our systems
            within 30 days.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            8. Data Security
          </h2>
          <p>
            We implement appropriate technical and organizational measures to
            protect your data, including encryption, secure servers, and regular
            security audits. However, no method of transmission over the
            internet is 100% secure.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            9. Children's Privacy
          </h2>
          <p>
            Our service is not directed to individuals under the age of 13. We
            do not knowingly collect personal data from children under 13. If
            you believe we have collected data from a child, please contact us
            immediately.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            10. Changes to This Policy
          </h2>
          <p>
            We may update this privacy policy from time to time. We will notify
            you of any changes by posting the new policy on this page and
            updating the "Last updated" date.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-white mb-3">
            11. Contact Us
          </h2>
          <p>
            If you have questions about this privacy policy or our data
            practices, please contact us at:{" "}
            <a
              href="mailto:privacy@collectarena.com"
              className="text-blue-400 hover:text-blue-300"
            >
              privacy@collectarena.com
            </a>
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
